import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import './SiteTour.css';

export interface SiteTourStep {
  /** data-tour attribute of the element to spotlight; omit for a centered card */
  target?: string;
  /** Optional workspace destination to open before spotlighting this step. */
  destination?: { index: number; subIndex: number };
  /**
   * Opens the target's own disclosure button before measuring, for panels that
   * ship collapsed. The panel stays open afterwards — the step just explained
   * what is inside it.
   */
  expand?: boolean;
  /** Omit to show the step to both supported workspace roles. */
  roles?: ('admin' | 'manager')[];
  eyebrow: string;
  title: string;
  body: string;
}

interface SiteTourProps {
  steps: SiteTourStep[];
  /** Called when the tour ends; `completed` is false when the user skipped. */
  onClose: (completed: boolean) => void;
  /** Lets a tour step move the shell before its target is measured. */
  onStepChange?: (step: SiteTourStep) => void;
}

const HIGHLIGHT_PADDING = 6;
const CARD_GAP = 18;
const VIEWPORT_MARGIN = 16;

export default function SiteTour({ steps, onClose, onStepChange }: SiteTourProps) {
  const { t } = useI18n();
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [cardPosition, setCardPosition] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );

  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const hasExpandedTarget = useRef(false);

  useLayoutEffect(() => {
    const targetName = step?.target;
    const shouldExpand = step?.expand ?? false;
    hasExpandedTarget.current = false;
    if (!targetName) {
      setTargetRect(null);
      return undefined;
    }

    let animationFrame: number | null = null;
    let observedTarget: Element | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const measureTarget = () => {
      animationFrame = null;
      const element = document.querySelector(`[data-tour="${targetName}"]`);

      if (element !== observedTarget) {
        if (observedTarget) resizeObserver?.unobserve(observedTarget);
        observedTarget = element;
        if (observedTarget) {
          resizeObserver?.observe(observedTarget);
          observedTarget.scrollIntoView({ block: 'nearest' });
        }
      }

      if (!element) {
        setTargetRect(null);
        return;
      }

      // Opening the panel mutates the DOM, which schedules another measurement;
      // the guard keeps that from becoming a click loop.
      if (shouldExpand && !hasExpandedTarget.current) {
        const toggle = element.querySelector<HTMLElement>('button[aria-expanded="false"]');
        if (toggle) {
          hasExpandedTarget.current = true;
          toggle.click();
          return;
        }
      }

      const rect = element.getBoundingClientRect();
      setTargetRect(rect.width > 0 && rect.height > 0 ? rect : null);
    };

    const scheduleMeasurement = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(measureTarget);
    };

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleMeasurement);
    }

    measureTarget();

    // Cross-page steps can render their target after data has loaded. Observe
    // DOM additions/removals so a temporarily missing target is picked up, and
    // keep the spotlight aligned if the page scrolls underneath the overlay.
    const mutationObserver = new MutationObserver(scheduleMeasurement);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', scheduleMeasurement);
    window.addEventListener('scroll', scheduleMeasurement, true);

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasurement);
      window.removeEventListener('scroll', scheduleMeasurement, true);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, [step?.target, step?.expand]);

  // Position the card beside the spotlight once both have rendered.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card || !targetRect) {
      setCardPosition(null);
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = targetRect.right + HIGHLIGHT_PADDING + CARD_GAP;
    if (left + card.offsetWidth > viewportWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, targetRect.left - card.offsetWidth - CARD_GAP);
    }

    const top = Math.min(
      Math.max(targetRect.top - HIGHLIGHT_PADDING, VIEWPORT_MARGIN),
      Math.max(viewportHeight - card.offsetHeight - VIEWPORT_MARGIN, VIEWPORT_MARGIN),
    );

    setCardPosition({ top, left });
  }, [targetRect, stepIndex]);

  useEffect(() => {
    nextButtonRef.current?.focus();
  }, [stepIndex]);

  useEffect(() => {
    const previousFocus = previousFocusRef.current;
    return () => previousFocus?.focus();
  }, []);

  const goNext = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      onClose(true);
    } else {
      const nextIndex = stepIndex + 1;
      onStepChange?.(steps[nextIndex]);
      setStepIndex(nextIndex);
    }
  }, [stepIndex, steps, onClose, onStepChange]);

  const goBack = useCallback(() => {
    const previousIndex = Math.max(0, stepIndex - 1);
    onStepChange?.(steps[previousIndex]);
    setStepIndex(previousIndex);
  }, [stepIndex, steps, onStepChange]);

  const skip = useCallback(() => onClose(false), [onClose]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        const card = cardRef.current;
        if (!card) return;
        const focusable = Array.from(card.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ));
        if (!focusable.length) {
          event.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const activeInside = card.contains(document.activeElement);
        if (event.shiftKey && (!activeInside || document.activeElement === first)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (!activeInside || document.activeElement === last)) {
          event.preventDefault();
          first.focus();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        skip();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goBack, skip]);

  if (!step) return null;

  const centered = !targetRect;

  return (
    <div className="site-tour" role="dialog" aria-modal="true" aria-label={t('Site tour')}>
      {targetRect ? (
        <div
          className="site-tour__spotlight"
          style={{
            top: targetRect.top - HIGHLIGHT_PADDING,
            left: targetRect.left - HIGHLIGHT_PADDING,
            width: targetRect.width + HIGHLIGHT_PADDING * 2,
            height: targetRect.height + HIGHLIGHT_PADDING * 2,
          }}
        />
      ) : (
        <div className="site-tour__backdrop" />
      )}

      <div
        key={stepIndex}
        ref={cardRef}
        className={`site-tour__card ${centered ? 'site-tour__card--centered' : ''}`}
        style={
          centered
            ? undefined
            : {
                top: cardPosition?.top ?? VIEWPORT_MARGIN,
                left: cardPosition?.left ?? VIEWPORT_MARGIN,
                visibility: cardPosition ? 'visible' : 'hidden',
              }
        }
      >
        <div className="site-tour__header">
          <span className="site-tour__eyebrow">{t(step.eyebrow)}</span>
          <span className="site-tour__count">{stepIndex + 1} / {steps.length}</span>
        </div>

        <h2 className="site-tour__title">{t(step.title)}</h2>
        <p className="site-tour__body">{t(step.body)}</p>

        <div className="site-tour__progress-track">
          <div
            className="site-tour__progress-fill"
            style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
          />
        </div>

        <div className="site-tour__actions">
          {!isLastStep && (
            <button type="button" className="site-tour__skip" onClick={skip}>
              {t('Skip tour')}
            </button>
          )}
          <div className="site-tour__nav-buttons">
            {stepIndex > 0 && (
              <button type="button" className="btn btn--secondary" onClick={goBack}>
                {t('Back')}
              </button>
            )}
            <button type="button" ref={nextButtonRef} className="btn btn--primary site-tour__next" onClick={goNext}>
              {isLastStep ? t('Get started') : t('Next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
